
/**
 * StrategicInitiative Integration Layer
 * Connects SDK Service to Zustand Store
 */
import i18next from 'i18next';
import { toast } from 'sonner';
import { useStrategicInitiativeStore } from '../../store/strategic-initiative.store.js';
import { Logger } from '@/lib/app-log.js';

export class StrategicInitiativeIntegration {
    constructor(sdkService) {
        this.service = sdkService;
    }

    /**
     * Fetch all items and update store
     */
    async fetchStrategicInitiatives(params = {}) {
        const store = useStrategicInitiativeStore.getState();
        
        if (store.setLoading) store.setLoading(true);
        if (store.clearError) store.clearError();

        try {
            const response = await this.service.getAll(params);
            
            // Handle various setter naming conventions
            if (store.setStrategicInitiatives) {
                store.setStrategicInitiatives(response.data);
            } else if (store.setItems) {
                store.setItems(response.data);
            } else if (store.setStrategicInitiatives) { // Fallback standard
                store.setStrategicInitiatives(response.data);
            }
            
            // Handle Pagination
            if (response.meta || response.pagination) {
                const pagination = response.meta || response.pagination;
                if (store.setPagination) store.setPagination(pagination);
                else if (store.setPaginationData) store.setPaginationData(pagination);
                else if (store.setParams) store.setParams({ pagination });
            }

            // Handle Unique Values (if applicable)
            if (response.uniqueValues && store.setUniqueValues) {
                store.setUniqueValues(response.uniqueValues);
            }

            return { success: true, data: response.data };
        } catch (error) {
            const message = this._getErrorMessage(error);
            if (store.setError) store.setError(message);
            toast.error(message);
            return { success: false, error: message };
        } finally {
            if (store.setLoading) store.setLoading(false);
        }
    }

    /**
     * Get by ID
     */
    async fetchStrategicInitiativeById(id) {
        const store = useStrategicInitiativeStore.getState();
        if (store.setLoading) store.setLoading(true);

        try {
            const response = await this.service.getById(id);
            
            // Try common naming for selecting single item
            if (store.setSelectedStrategicInitiative) {
                store.setSelectedStrategicInitiative(response.data);
            } else if (store.setCurrentStrategicInitiative) {
                store.setCurrentStrategicInitiative(response.data);
            } else if (store.setSelectedItem) {
                store.setSelectedItem(response.data);
            }

            return { success: true, data: response.data };
        } catch (error) {
            const message = this._getErrorMessage(error);
            toast.error(message);
            return { success: false, error: message };
        } finally {
            if (store.setLoading) store.setLoading(false);
        }
    }

    /**
     * Create item
     */
    async createStrategicInitiative(data) {
        const store = useStrategicInitiativeStore.getState();
        if (store.setLoading) store.setLoading(true);

        try {
            const response = await this.service.create(data);
            
            toast.success(i18next.t('integrationToasts:strategicinitiativeCreated', { defaultValue: 'StrategicInitiative created' }));
            
            // Optional: Close form if store has that action
            if (store.closeForm) store.closeForm();
            if (store.cancelEditing) store.cancelEditing();

            return { success: true, data: response.data };
        } catch (error) {
            const message = this._getErrorMessage(error);
            toast.error(message);
            return { success: false, error: message };
        } finally {
            if (store.setLoading) store.setLoading(false);
        }
    }

    /**
     * Update item
     */
    async updateStrategicInitiative(id, data) {
        const store = useStrategicInitiativeStore.getState();
        if (store.setLoading) store.setLoading(true);

        try {
            const response = await this.service.update(id, data);
            
            toast.success(i18next.t('integrationToasts:strategicinitiativeUpdated', { defaultValue: 'StrategicInitiative updated' }));
            
            if (store.closeForm) store.closeForm();
            if (store.cancelEditing) store.cancelEditing();

            return { success: true, data: response.data };
        } catch (error) {
            const message = this._getErrorMessage(error);
            toast.error(message);
            return { success: false, error: message };
        } finally {
            if (store.setLoading) store.setLoading(false);
        }
    }

    /**
     * Delete item
     */
    async deleteStrategicInitiative(id) {
        const store = useStrategicInitiativeStore.getState();
        if (store.setLoading) store.setLoading(true);

        try {
            const response = await this.service.delete(id);
            
            toast.success(i18next.t('integrationToasts:strategicinitiativeDeleted', { defaultValue: 'StrategicInitiative deleted' }));
            
            if (store.closeDeleteDialog) store.closeDeleteDialog();

            return { success: true };
        } catch (error) {
            const message = this._getErrorMessage(error);
            toast.error(message);
            return { success: false, error: message };
        } finally {
            if (store.setLoading) store.setLoading(false);
        }
    }

    async fetchPartyTemplate(params = {}) {
        try {
            const response = await this.service.getPartyTemplate(params);
            return { success: true, data: response?.data ?? response };
        } catch (error) {
            return { success: false, error: this._getErrorMessage(error) };
        }
    }

    async fetchParty(id) {
        try {
            const response = await this.service.getParty(id);
            return { success: true, data: response?.data ?? response, partyTemplate: response?.partyTemplate };
        } catch (error) {
            return { success: false, error: this._getErrorMessage(error) };
        }
    }

    async syncParty(id, body) {
        try {
            const response = await this.service.syncParty(id, body);
            toast.success(i18next.t('integrationToasts:ownershipUpdated', { defaultValue: 'Ownership updated' }));
            return { success: true, data: response?.data ?? response };
        } catch (error) {
            const message = this._getErrorMessage(error);
            toast.error(message);
            return { success: false, error: message };
        }
    }

    _getErrorMessage(error) {
         if (error.isValidationError?.()) {
            return 'Please check the form data';
        }
        return error.message || 'An error occurred';
    }
}
